/**
 * MgSwimRaceScene — wyścig pływacki (§14.5 / R31).
 * Style → dystans → button-mash (cap 12/s) → 2 NPC z gumowaniem → medal.
 * Brak przegranej. Nagroda wyłącznie przez core/RunClient.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { runClient } from '../../core/RunClient.js';
import {
  SWIM_STYLES,
  styleById,
  canAcceptTap,
  applyImpulse,
  dampVelocity,
  advancePosition,
  rubberBandNpc,
  type SwimStyleId,
  type SwimStyleDef,
} from './swimPhysics.js';
import {
  DISTANCE_OPTIONS,
  evaluateRace,
  trackLengthUnits,
  npcBaseSpeed,
  type RaceDistance,
  type RaceResult,
} from './raceLogic.js';

type Phase = 'style' | 'distance' | 'race' | 'result';

type EntryData = {
  entryPoint?: 'world' | 'console' | 'phone';
};

const LANE_Y = [280, 380, 480] as const; // gracz, NPC0, NPC1
const TRACK_X0 = 120;
const TRACK_X1 = 1160;

export class MgSwimRaceScene extends BaseScene {
  static readonly KEY = 'MgSwimRace';

  private phase: Phase = 'style';
  private styleId: SwimStyleId = 'piesek';
  private lengths: RaceDistance = 1;
  private uiRoot: Phaser.GameObjects.Container | null = null;

  private runId: string | null = null;
  private raceStartedAt = 0;
  private velocity = 0;
  private playerPos = 0;
  private npcPos = [0, 0];
  private totalTaps = 0;
  private windowStartMs = 0;
  private tapsInWindow = 0;
  private style!: SwimStyleDef;
  private finishAt = 0;
  private racing = false;
  private finishing = false;

  private playerSprite: Phaser.GameObjects.Container | null = null;
  private npcSprites: Phaser.GameObjects.Container[] = [];
  private progressFill: Phaser.GameObjects.Rectangle | null = null;
  private mashBtn: Phaser.GameObjects.Container | null = null;
  private tapLabel: Phaser.GameObjects.Text | null = null;
  private spaceKey: Phaser.Input.Keyboard.Key | null = null;

  constructor() {
    super(MgSwimRaceScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_pool_lane');
  }

  create(data: EntryData = {}): void {
    void data;
    // WP13: realne tło torów (bg_pool_lane) — trwałe, pod wymienianym UI
    this.addRealBg('bg_pool_lane');
    this.phase = 'style';
    this.styleId = 'piesek';
    this.lengths = 1;
    this.runId = null;
    this.racing = false;
    this.finishing = false;
    this.totalTaps = 0;
    this.velocity = 0;
    this.playerPos = 0;
    this.npcPos = [0, 0];

    this.cameras.main.setBackgroundColor(0x4fa3d1);
    this.registry.set('companionFollow', {
      x: this.gameW - 90,
      y: this.gameH - 90,
    });
    this.events.once('shutdown', () => {
      this.registry.remove('companionFollow');
      if (this.spaceKey && this.input.keyboard) {
        this.input.keyboard.removeKey(this.spaceKey);
      }
      this.spaceKey = null;
    });

    this.showStyleSelect();
  }

  update(_t: number, dtMs: number): void {
    if (!this.racing || this.finishing) return;
    const dt = Math.min(0.05, dtMs / 1000);
    const elapsed = (performance.now() - this.raceStartedAt) / 1000;

    // gracz: tłumienie + postęp
    this.velocity = dampVelocity(this.velocity, this.style);
    this.playerPos = advancePosition(this.playerPos, this.velocity, dt);

    // NPC: bazowa prędkość + gumowanie
    for (let i = 0; i < 2; i++) {
      const spd = npcBaseSpeed(i, elapsed);
      this.npcPos[i] = (this.npcPos[i] ?? 0) + spd * dt;
      this.npcPos[i] = rubberBandNpc(this.npcPos[i]!, this.playerPos);
      // nie przekraczaj mety zanim gracz skończy (przyjazne — mogą finiszować)
      this.npcPos[i] = Math.min(this.npcPos[i]!, this.finishAt + 2);
    }

    this.syncSprites();

    if (this.playerPos >= this.finishAt) {
      void this.onFinish();
    }
  }

  // ─── UI phases ─────────────────────────────────────────────

  private clearUi(): void {
    this.uiRoot?.destroy(true);
    this.uiRoot = null;
    this.playerSprite = null;
    this.npcSprites = [];
    this.progressFill = null;
    this.mashBtn = null;
    this.tapLabel = null;
  }

  private showStyleSelect(): void {
    this.clearUi();
    this.phase = 'style';
    const kids: Phaser.GameObjects.GameObject[] = [
      ...this.makeHeader('Wybierz styl pływania', 'Każdy styl jest super! ✨'),
    ];

    const startX = this.gameW / 2 - 260;
    SWIM_STYLES.forEach((s, i) => {
      const x = startX + i * 260;
      const y = 360;
      const bg = this.add
        .rectangle(0, 0, 220, 200, 0xfff8f0)
        .setStrokeStyle(4, 0xffffff)
        .setInteractive({ useHandCursor: true });
      const emoji = this.add.text(0, -40, s.emoji, { fontSize: '56px' }).setOrigin(0.5);
      const label = this.add
        .text(0, 40, s.labelPl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const c = this.add.container(x, y, [bg, emoji, label]);
      c.setSize(220, 200);
      bg.on('pointerdown', () => {
        this.styleId = s.id;
        this.showDistanceSelect();
      });
      kids.push(c);
    });

    kids.push(this.makeBackToPool());
    this.uiRoot = this.add.container(0, 0, kids);
  }

  private showDistanceSelect(): void {
    this.clearUi();
    this.phase = 'distance';
    const kids: Phaser.GameObjects.GameObject[] = [
      ...this.makeHeader('Wybierz dystans', 'Dłuższy tor = więcej gwiazdek ⭐'),
    ];
    DISTANCE_OPTIONS.forEach((d, i) => {
      const y = 280 + i * 100;
      const btn = UiKit.button(this, {
        x: this.gameW / 2,
        y,
        label: d.labelPl,
        width: 360,
        height: Math.max(72, MIN_TOUCH),
        fill: i === 0 ? 0xb8e6d0 : i === 1 ? 0xffd6a5 : 0xc9b1ff,
        onClick: () => {
          this.lengths = d.lengths;
          void this.beginRace();
        },
      });
      kids.push(btn);
    });
    kids.push(this.makeBackToPool());
    this.uiRoot = this.add.container(0, 0, kids);
  }

  private async beginRace(): Promise<void> {
    this.clearUi();
    this.phase = 'race';
    this.style = styleById(this.styleId);
    this.finishAt = trackLengthUnits(this.lengths);
    this.playerPos = 0;
    this.npcPos = [0, 0];
    this.velocity = 0;
    this.totalTaps = 0;
    this.windowStartMs = performance.now();
    this.tapsInWindow = 0;
    this.finishing = false;

    // startRun — nagroda tylko z runId
    try {
      const { runId } = await runClient.startRun('swimrace');
      this.runId = runId;
    } catch {
      this.runId = null; // offline: gwiazdki bez zł (§7.5)
    }

    this.drawRaceTrack();
    this.raceStartedAt = performance.now();
    this.racing = true;

    // spacja = impuls
    if (this.input.keyboard) {
      this.spaceKey = this.input.keyboard.addKey(
        Phaser.Input.Keyboard.KeyCodes.SPACE,
      );
      this.spaceKey.on('down', () => this.onTap());
    }
  }

  private drawRaceTrack(): void {
    const kids: Phaser.GameObjects.GameObject[] = [];

    // WP13: realne tło (bg_pool_lane) zamiast pełnoekranowego prostokąta wody
    // pasy torów
    for (const y of LANE_Y) {
      kids.push(
        this.add
          .rectangle(this.gameW / 2, y, TRACK_X1 - TRACK_X0 + 40, 70, 0x3d8ebd, 0.5)
          .setStrokeStyle(2, 0xffffff, 0.4),
      );
    }
    // start / meta
    kids.push(this.add.rectangle(TRACK_X0, 380, 8, 280, 0xffffff));
    kids.push(this.add.rectangle(TRACK_X1, 380, 8, 280, 0xffd700));
    kids.push(
      this.add
        .text(TRACK_X1, 220, 'META', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#ffd700',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    kids.push(
      this.add
        .text(this.gameW / 2, 40, `Wyścig — ${this.lengths * 25} m · ${this.style.emoji} ${this.style.labelPl}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '26px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    // pasek postępu
    kids.push(
      this.add
        .rectangle(this.gameW / 2, 80, 400, 16, 0xffffff, 0.35)
        .setOrigin(0.5),
    );
    this.progressFill = this.add
      .rectangle(this.gameW / 2 - 200, 80, 4, 16, 0xffd700)
      .setOrigin(0, 0.5);
    kids.push(this.progressFill);

    // pływacy
    this.playerSprite = this.makeSwimmer(LANE_Y[0], '🐶', 0xffe4e1, 'Ty');
    this.npcSprites = [
      this.makeSwimmer(LANE_Y[1], '🐕', 0xbde0fe, 'Pola'),
      this.makeSwimmer(LANE_Y[2], '🐩', 0xffd6a5, 'Burek'),
    ];
    kids.push(this.playerSprite, ...this.npcSprites);

    // duży przycisk-fala + dolna połowa ekranu
    const mashH = Math.max(120, MIN_TOUCH * 1.5);
    const mashY = this.gameH - 70;
    const mashBg = this.add
      .rectangle(0, 0, 420, mashH, 0x90e0ef)
      .setStrokeStyle(5, 0xffffff)
      .setInteractive({ useHandCursor: true });
    const mashLabel = this.add
      .text(0, 0, '🌊 PŁYŃ! 🌊', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.mashBtn = this.add.container(this.gameW / 2, mashY, [mashBg, mashLabel]);
    this.mashBtn.setSize(420, mashH);
    this.mashBtn.setDepth(100);
    mashBg.on('pointerdown', () => this.onTap());
    kids.push(this.mashBtn);

    // cała dolna połowa ekranu = hit area
    const half = this.add
      .rectangle(
        this.gameW / 2,
        this.gameH * 0.75,
        this.gameW,
        this.gameH / 2,
        0xffffff,
        0.001,
      )
      .setInteractive();
    half.on('pointerdown', () => this.onTap());
    half.setDepth(50);
    kids.push(half);

    this.tapLabel = this.add
      .text(this.gameW / 2, 120, 'Stukaj szybko! (spacja też)', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    kids.push(this.tapLabel);

    this.uiRoot = this.add.container(0, 0, kids);
    this.syncSprites();
  }

  private makeSwimmer(
    y: number,
    emoji: string,
    color: number,
    name: string,
  ): Phaser.GameObjects.Container {
    const body = this.add.circle(0, 0, 28, color).setStrokeStyle(3, 0xffffff);
    const face = this.add.text(0, 0, emoji, { fontSize: '28px' }).setOrigin(0.5);
    const tag = this.add
      .text(0, 36, name, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const c = this.add.container(TRACK_X0, y, [body, face, tag]);
    c.setDepth(20);
    return c;
  }

  private syncSprites(): void {
    const mapX = (pos: number) => {
      const t = Math.min(1, Math.max(0, pos / this.finishAt));
      return TRACK_X0 + t * (TRACK_X1 - TRACK_X0);
    };
    if (this.playerSprite) {
      this.playerSprite.x = mapX(this.playerPos);
      // lekki bobbing w rytmie velocity
      this.playerSprite.y =
        LANE_Y[0] + Math.sin(performance.now() / 120) * Math.min(6, this.velocity * 2);
    }
    this.npcSprites.forEach((s, i) => {
      s.x = mapX(this.npcPos[i] ?? 0);
      s.y = LANE_Y[i + 1]! + Math.sin(performance.now() / 140 + i) * 3;
    });
    if (this.progressFill) {
      const t = Math.min(1, this.playerPos / this.finishAt);
      this.progressFill.width = Math.max(4, 400 * t);
    }
  }

  private onTap(): void {
    if (!this.racing || this.finishing) return;
    const now = performance.now();
    const gate = canAcceptTap(now, this.windowStartMs, this.tapsInWindow);
    this.windowStartMs = gate.windowStartMs;
    this.tapsInWindow = gate.tapsInWindow;
    if (!gate.accept) return;

    this.totalTaps += 1;
    this.velocity = applyImpulse(this.velocity, this.style);

    if (this.mashBtn) {
      this.tweens.add({
        targets: this.mashBtn,
        scaleX: 0.94,
        scaleY: 0.94,
        duration: 60,
        yoyo: true,
      });
    }
    if (this.tapLabel) {
      this.tapLabel.setText(`Tapy: ${this.totalTaps} · v=${this.velocity.toFixed(1)}`);
    }
  }

  private async onFinish(): Promise<void> {
    if (this.finishing) return;
    this.finishing = true;
    this.racing = false;
    this.playerPos = this.finishAt;

    const durationMs = Math.max(1, Math.floor(performance.now() - this.raceStartedAt));
    const result = evaluateRace({
      lengths: this.lengths,
      playerPos: this.playerPos,
      npcPositions: this.npcPos,
      taps: this.totalTaps,
      durationMs,
    });

    let rewardZl = 0;
    let balance = 0;
    if (this.runId) {
      try {
        // Surowe tapy — serwer capuje tempo (§7.2 / clampTaps)
        const fin = await runClient.finishRun(this.runId, {
          stars: result.stars,
          score: result.score,
          durationMs,
          taps: this.totalTaps,
        });
        rewardZl = fin.rewardZl;
        balance = fin.balance;
      } catch {
        // offline / błąd sieci — pokazujemy medal bez zł
      }
    }

    this.showResult(result, rewardZl, balance);
  }

  private showResult(result: RaceResult, rewardZl: number, balance: number): void {
    this.clearUi();
    this.phase = 'result';

    const medalEmoji =
      result.medal === 'gold' ? '🥇' : result.medal === 'silver' ? '🥈' : '😊';

    const kids: Phaser.GameObjects.GameObject[] = [];
    kids.push(
      this.add
        .rectangle(this.gameW / 2, this.gameH / 2, this.gameW, this.gameH, 0x7ec8e3),
    );
    kids.push(
      this.add
        .text(this.gameW / 2, 160, 'Meta!', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '48px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    kids.push(
      this.add
        .text(this.gameW / 2, 250, `${medalEmoji} ${result.medalLabelPl}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '36px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    kids.push(
      this.add
        .text(
          this.gameW / 2,
          320,
          `Miejsce ${result.place} · ${'⭐'.repeat(Math.max(1, result.stars))}`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '28px',
            color: '#6b5f7a',
          },
        )
        .setOrigin(0.5),
    );

    const rewardLine =
      rewardZl > 0
        ? `+${rewardZl} zł  (saldo ${balance})`
        : result.goodGame
          ? 'Świetna gra! 🌟'
          : 'Brawo za dopłynięcie! 💪';
    kids.push(
      this.add
        .text(this.gameW / 2, 380, rewardLine, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '24px',
          color: '#2d6a4f',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    // Zawsze pozytywne zakończenie — brak przegranej
    kids.push(
      this.add
        .text(this.gameW / 2, 430, 'Każdy wyścig to super zabawa!', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#6b5f7a',
        })
        .setOrigin(0.5),
    );

    const again = UiKit.button(this, {
      x: this.gameW / 2 - 140,
      y: 540,
      label: 'Jeszcze raz',
      width: 220,
      height: MIN_TOUCH,
      fill: 0x90e0ef,
      onClick: () => this.showStyleSelect(),
    });
    const pool = UiKit.button(this, {
      x: this.gameW / 2 + 140,
      y: 540,
      label: 'Do basenu',
      width: 220,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => getSceneRouter().go('Pool'),
    });
    kids.push(again, pool);

    // opcjonalnie ekran Reward z core gdy jest zł
    if (rewardZl > 0) {
      const rewardBtn = UiKit.button(this, {
        x: this.gameW / 2,
        y: 620,
        label: `Zobacz nagrodę (+${rewardZl} zł)`,
        width: 320,
        height: MIN_TOUCH,
        fill: 0xffd700,
        onClick: () =>
          getSceneRouter().go('Reward', {
            rewardZl,
            returnTo: 'Pool',
          }),
      });
      kids.push(rewardBtn);
    }

    this.uiRoot = this.add.container(0, 0, kids);
  }

  private makeHeader(
    title: string,
    subtitle: string,
  ): Phaser.GameObjects.GameObject[] {
    const t = this.add
      .text(this.gameW / 2, 80, title, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const s = this.add
      .text(this.gameW / 2, 130, subtitle, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#e8f4fc',
      })
      .setOrigin(0.5);
    return [t, s];
  }

  private makeBackToPool(): Phaser.GameObjects.Container {
    const btn = UiKit.button(this, {
      x: 120,
      y: 48,
      label: '← Basen',
      width: 160,
      height: MIN_TOUCH,
      fill: 0xb8e6d0,
      onClick: () => getSceneRouter().go('Pool'),
    });
    btn.setName('hotspot-back-Pool');
    return btn;
  }
}

export default MgSwimRaceScene;
